import styled, { keyframes } from 'styled-components';

import { SKELETON } from '../../theme/tokens';

// A single faint highlight band that travels left→right across the block, then
// holds off-screen for the back half of the cycle so it glimmers once and rests
// (not a constant shine). Driven on a `::after` overlay that only transforms, so
// it animates on the compositor and never repaints the block. Honoured only when
// motion is allowed (see the reduced-motion guard below).
const sheen = keyframes`
  0% {
    transform: translateX(-100%);
  }
  55%,
  100% {
    transform: translateX(100%);
  }
`;

// The block eases up from transparent the instant it mounts, so a placeholder that
// appears (a section starting to load) arrives as a gentle fade rather than a pop.
// Runs once; `forwards` holds the resolved opacity so it never flickers back.
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export type SkeletonShape = { $w: string; $h: string; $radius: string; $block: boolean };

export const Block = styled.span<SkeletonShape>`
  display: ${(p) => (p.$block ? 'block' : 'inline-block')};
  width: ${(p) => p.$w};
  height: ${(p) => p.$h};
  border-radius: ${(p) => p.$radius};
  background-color: ${SKELETON.base};
  /* Keeps an inline placeholder aligned with the glyphs it stands in for. */
  vertical-align: middle;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} ${SKELETON.FADE_IN_MS}ms ease both;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    /* A soft diagonal glimmer — transparent at the edges so only a hint passes. */
    background: linear-gradient(100deg, transparent 35%, ${SKELETON.sheen} 50%, transparent 65%);
    transform: translateX(-100%);
    animation: ${sheen} ${SKELETON.SHEEN_MS}ms ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    /* No fade-in and no travelling sheen — the placeholder just appears. */
    animation: none;
    &::after {
      animation: none;
    }
  }
`;
